<template>
  <div class="gs-app gm-gdpr p-6">
    <div class="max-w-5xl mx-auto">
      <div class="mb-6">
        <h1 class="text-2xl font-semibold mb-2">Privacy and data requests</h1>
        <p class="text-sm text-grey-600">
          Your Groove account is the controller for this data. GrooveMail is the
          processor that gives you the export, erasure, and retention controls
          you need to answer a data-subject request. Retention durations and
          lawful-basis wording require your counsel's approval.
        </p>
      </div>

      <div class="flex border-b-2 border-grey-200 mb-4">
        <button
          v-for="tab in tabs"
          :key="tab"
          class="px-4 py-2 text-sm"
          :class="activeTab === tab ? 'border-b-2 border-primary text-primary font-semibold' : 'text-grey-600'"
          @click="activeTab = tab"
        >
          {{ tab === 'requests' ? 'Requests' : 'Retention' }}
        </button>
      </div>

      <div v-if="activeTab === 'requests'">
        <div class="bg-white border border-grey-200 rounded p-4 mb-6">
          <h2 class="text-base font-semibold mb-3">Log a data-subject request</h2>
          <div class="flex flex-wrap items-end gap-3">
            <label class="flex flex-col text-xs text-grey-600">
              Request type
              <select v-model="form.type" class="border border-grey-300 rounded px-2 py-1 mt-1 text-sm">
                <option v-for="type in requestTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </label>
            <label class="flex flex-col text-xs text-grey-600">
              Subject email
              <input
                v-model="form.subject_email"
                class="border border-grey-300 rounded px-2 py-1 mt-1 text-sm w-72"
                placeholder="subject@example.com"
                type="email"
              />
            </label>
            <label class="flex flex-col text-xs text-grey-600">
              Note
              <input
                v-model="form.requester_note"
                class="border border-grey-300 rounded px-2 py-1 mt-1 text-sm w-72"
                placeholder="Verified via account email"
              />
            </label>
            <button
              class="bg-primary text-white rounded px-4 py-2 text-sm disabled:opacity-50"
              :disabled="creating || !form.subject_email"
              @click="createRequest"
            >
              {{ creating ? 'Saving…' : 'Log request' }}
            </button>
          </div>
          <p v-if="error" class="text-sm text-red-600 mt-3">{{ error }}</p>
        </div>

        <div class="bg-white border border-grey-200 rounded">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-grey-200 text-left text-grey-600">
                <th class="p-3">Subject</th>
                <th class="p-3">Type</th>
                <th class="p-3">Status</th>
                <th class="p-3">Due</th>
                <th class="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!requests.length">
                <td class="p-4 text-grey-600" colspan="5">
                  No privacy requests yet.
                </td>
              </tr>
              <tr v-for="item in requests" :key="item.id" class="border-b border-grey-100">
                <td class="p-3">{{ item.subject_email }}</td>
                <td class="p-3">{{ item.type }}</td>
                <td class="p-3">
                  <select
                    v-model="item.status"
                    class="border border-grey-300 rounded px-2 py-1 text-sm"
                    @change="updateStatus(item)"
                  >
                    <option v-for="status in requestStatuses" :key="status" :value="status">
                      {{ status }}
                    </option>
                  </select>
                </td>
                <td class="p-3 text-grey-600">{{ formatDate(item.due_at) }}</td>
                <td class="p-3 text-right">
                  <button
                    v-if="item.type === 'ACCESS'"
                    class="border border-grey-300 rounded px-3 py-1 text-sm mr-2"
                    @click="runExport(item)"
                  >
                    Export
                  </button>
                  <button
                    v-if="item.type === 'ERASURE'"
                    class="border border-grey-300 rounded px-3 py-1 text-sm"
                    @click="reviewErasure(item)"
                  >
                    Review erasure
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="exportPackage" class="mt-6 bg-white border border-grey-200 rounded p-4">
          <h2 class="text-base font-semibold mb-2">Subject export</h2>
          <pre class="text-xs overflow-auto max-h-72 bg-grey-100 p-3 rounded">{{ JSON.stringify(exportPackage.counts, null, 2) }}</pre>
          <button class="border border-grey-300 rounded px-3 py-1 text-sm mt-3" @click="downloadExport">
            Download JSON
          </button>
        </div>

        <div v-if="erasurePlan" class="mt-6 bg-white border border-grey-200 rounded p-4">
          <h2 class="text-base font-semibold mb-2">Erasure plan</h2>
          <pre class="text-xs overflow-auto bg-grey-100 p-3 rounded">{{ JSON.stringify(erasurePlan.counts, null, 2) }}</pre>
          <p class="text-xs text-grey-600 mt-2">
            Leads and their links are deleted, delivery rows are removed, and one
            suppression remains so the address is never mailed again.
          </p>
          <input
            v-model="confirmEmail"
            class="border border-grey-300 rounded px-2 py-1 text-sm w-72 mt-3"
            :placeholder="erasureTarget ? erasureTarget.subject_email : ''"
          />
          <button
            class="bg-red-600 text-white rounded px-4 py-2 text-sm ml-2 mt-3 disabled:opacity-50"
            :disabled="erasing || normalize(confirmEmail) !== normalize(erasureTarget && erasureTarget.subject_email)"
            @click="executeErasure"
          >
            {{ erasing ? 'Erasing…' : 'Erase subject' }}
          </button>
        </div>
      </div>

      <div v-else>
        <div class="bg-white border border-grey-200 rounded p-4">
          <h2 class="text-base font-semibold mb-3">Retention schedule</h2>
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-grey-200 text-left text-grey-600">
                <th class="p-3">Data class</th>
                <th class="p-3">Retention (days)</th>
                <th class="p-3">Enabled</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entry, key) in retention" :key="key" class="border-b border-grey-100">
                <td class="p-3">
                  {{ key }}
                  <span v-if="entry.is_default" class="text-xs text-grey-500 ml-2">default</span>
                </td>
                <td class="p-3">
                  <input
                    v-model.number="entry.retention_days"
                    class="border border-grey-300 rounded px-2 py-1 text-sm w-24"
                    min="1"
                    type="number"
                  />
                </td>
                <td class="p-3">
                  <input v-model="entry.enabled" type="checkbox" />
                </td>
              </tr>
            </tbody>
          </table>
          <button
            class="bg-primary text-white rounded px-4 py-2 text-sm mt-4 disabled:opacity-50"
            :disabled="saving || keyPolicyIsProtected"
            @click="saveRetention"
          >
            {{ saving ? 'Saving…' : 'Save retention policy' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getData, postData, putData, patchData } from '@/utils/apiHelper';

export default {
  name: 'GdprWorkbench',
  data() {
    return {
      tabs: ['requests', 'retention'],
      activeTab: 'requests',
      requestTypes: ['ACCESS', 'ERASURE', 'RECTIFICATION', 'RESTRICTION', 'OBJECTION', 'PORTABILITY'],
      requestStatuses: ['RECEIVED', 'VERIFYING', 'IN_PROGRESS', 'COMPLETED', 'PARTIALLY_COMPLETED', 'REJECTED'],
      form: { type: 'ACCESS', subject_email: '', requester_note: '' },
      requests: [],
      retention: {},
      exportPackage: null,
      erasurePlan: null,
      erasureTarget: null,
      confirmEmail: '',
      creating: false,
      saving: false,
      erasing: false,
      error: ''
    };
  },
  computed: {
    keyPolicyIsProtected() {
      return false;
    }
  },
  mounted() {
    this.loadRequests();
    this.loadRetention();
  },
  methods: {
    normalize(value) {
      return (value || '').trim().toLowerCase();
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString() : '—';
    },
    async loadRequests() {
      try {
        const response = await getData('/gdpr/requests', { handlesServerError: true }, true);
        this.requests = response.data.data.requests || [];
      } catch (e) {
        this.error = 'Could not load privacy requests.';
      }
    },
    async loadRetention() {
      try {
        const response = await getData('/gdpr/retention', { handlesServerError: true }, true);
        this.retention = response.data.data.policy || {};
      } catch (e) {
        this.error = 'Could not load the retention policy.';
      }
    },
    async createRequest() {
      this.creating = true;
      this.error = '';
      try {
        await postData('/gdpr/requests', this.form, { handlesServerError: true }, true);
        this.form.subject_email = '';
        this.form.requester_note = '';
        await this.loadRequests();
      } catch (e) {
        this.error = 'Could not log the request.';
      } finally {
        this.creating = false;
      }
    },
    async updateStatus(item) {
      try {
        await patchData(`/gdpr/requests/${item.id}`, { status: item.status }, true);
      } catch (e) {
        this.error = 'Could not update the request status.';
      }
    },
    async runExport(item) {
      try {
        const response = await getData(`/gdpr/requests/${item.id}/export`, { handlesServerError: true }, true);
        this.exportPackage = response.data.data.package;
        this.erasurePlan = null;
      } catch (e) {
        this.error = 'Export is only available for ACCESS requests.';
      }
    },
    downloadExport() {
      if (!this.exportPackage) return;
      const blob = new Blob([JSON.stringify(this.exportPackage, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `groovemail-subject-export-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
    async reviewErasure(item) {
      try {
        const response = await getData(`/gdpr/requests/${item.id}/erasure`, { handlesServerError: true }, true);
        this.erasurePlan = response.data.data.plan;
        this.erasureTarget = item;
        this.confirmEmail = '';
        this.exportPackage = null;
      } catch (e) {
        this.error = 'Erasure planning is only available for ERASURE requests.';
      }
    },
    async executeErasure() {
      if (!this.erasureTarget) return;
      this.erasing = true;
      try {
        await postData(`/gdpr/requests/${this.erasureTarget.id}/erasure`, {
          confirm_email: this.confirmEmail,
          acknowledge_irreversible: true
        }, { handlesServerError: true }, true);
        this.erasurePlan = null;
        this.erasureTarget = null;
        this.confirmEmail = '';
        await this.loadRequests();
      } catch (e) {
        this.error = 'Erasure failed. The confirmation email must match the subject email.';
      } finally {
        this.erasing = false;
      }
    },
    async saveRetention() {
      this.saving = true;
      try {
        const policies = Object.keys(this.retention).map((key) => ({
          data_class: key,
          retention_days: this.retention[key].retention_days,
          enabled: this.retention[key].enabled
        }));
        const response = await putData('/gdpr/retention', { policies }, true);
        this.retention = response.data.data.policy || this.retention;
      } catch (e) {
        this.error = 'Could not save the retention policy.';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
